import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Play a click sound when navigating the carousel.
 */
function playClickSound(audioRef: React.RefObject<HTMLAudioElement | null>) {
  if (audioRef.current) {
    audioRef.current.currentTime = 0;
    audioRef.current.volume = 0.3;
    audioRef.current.play().catch(() => {
      // Ignore autoplay errors
    });
  }
}

interface MfersBeheadedNewLandingProps {
  twitterUser?: unknown;
  onTwitterSignIn?: () => void;
  onTwitterSignOut?: () => void;
}

interface Level {
  id: string;
  name: string;
  image: string;
  route: string;
}

const LEVELS: Level[] = [
  {
    id: '1',
    name: 'Mfers Jus Getting Started',
    image: '/images/mfersbeheaded/declarationofcc0.webp',
    route: '/mfersbeheaded/level/1',
  },
  {
    id: '2',
    name: 'House Songs',
    image: '/images/mfersbeheaded/thumbnails/housesongs.png',
    route: '/mfersbeheaded/level/2',
  },
  {
    id: '4',
    name: 'Lil Mfers Playground',
    image: '/images/mfersbeheaded/thumbnails/mfa.png',
    route: '/mfersbeheaded/level/4',
  },
  {
    id: 'pixelcrush',
    name: 'Pixel Crush',
    image: '/images/mfersbeheaded/thumbnails/nakabg.png',
    route: '/mfersbeheaded/pixelcrush',
  },
  {
    id: 'tinydinos',
    name: "Let's F'ing Rawr",
    image: '/images/mfersbeheaded/thumbnails/tinydiners.jpeg',
    route: '/mfersbeheaded/tinydinos',
  },
];

/**
 * Generate a random display name for anonymous players.
 */
function generateRandomName(): string {
  const adjectives = ['Swift', 'Bold', 'Chill', 'Epic', 'Lucky', 'Brave'];
  const nouns = ['Mfer', 'Player', 'Gamer', 'Hero', 'Champion', 'Legend'];
  const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
  const noun = nouns[Math.floor(Math.random() * nouns.length)];
  const num = Math.floor(Math.random() * 999);
  return `${adj}${noun}${num}`;
}

/**
 * Gear icon for settings.
 */
const GearIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
    <path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
  </svg>
);

/**
 * Chevron icons for carousel navigation.
 */
const ChevronLeft = () => (
  <svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor">
    <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
  </svg>
);

const ChevronRight = () => (
  <svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor">
    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
  </svg>
);

/**
 * New mfersbeheaded landing page with level carousel.
 */
function MfersBeheadedNewLanding(_props: MfersBeheadedNewLandingProps) {
  const navigate = useNavigate();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const clickSoundRef = useRef<HTMLAudioElement | null>(null);

  const selectedLevel = LEVELS[selectedIndex];

  const handlePrev = useCallback(() => {
    playClickSound(clickSoundRef);
    setSelectedIndex((prev) => (prev > 0 ? prev - 1 : LEVELS.length - 1));
  }, []);

  const handleNext = useCallback(() => {
    playClickSound(clickSoundRef);
    setSelectedIndex((prev) => (prev < LEVELS.length - 1 ? prev + 1 : 0));
  }, []);

  // Handle touch swipe for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlePrev, handleNext]);

  const handlePlay = () => {
    const playerName = generateRandomName();
    sessionStorage.setItem('mfersbeheaded_playerName', playerName);
    navigate(selectedLevel.route);
  };

  const handleLeaderboard = () => {
    navigate('/mfersbeheaded/leaderboard');
  };

  const handleSettings = () => {
    navigate('/mfersbeheaded/settings');
  };

  return (
    <div style={styles.container}>
      {/* Click sound for carousel navigation */}
      <audio ref={clickSoundRef} src="/audio/mfersbeheaded/coindrop.mp3" preload="auto" />

      {/* Header - centered with larger text */}
      <header style={styles.header}>
        <img
          src="/images/mfersbeheaded/heads/3.png"
          alt="Mfer"
          style={styles.headerLogo}
        />
        <h1 style={styles.headerTitle}>mfers beheaded</h1>
      </header>

      {/* Level Carousel */}
      <div style={styles.carouselSection}>
        <button style={styles.carouselButton} onClick={handlePrev}>
          <ChevronLeft />
        </button>

        <div
          ref={carouselRef}
          style={styles.carouselContainer}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <img
            src={selectedLevel.image}
            alt={selectedLevel.name}
            style={styles.levelImage}
          />
        </div>

        <button style={styles.carouselButton} onClick={handleNext}>
          <ChevronRight />
        </button>
      </div>

      {/* Level Name */}
      <div style={styles.levelName}>{selectedLevel.name}</div>

      {/* Dots indicator */}
      <div style={styles.dotsContainer}>
        {LEVELS.map((_, index) => (
          <button
            key={index}
            style={{
              ...styles.dot,
              ...(index === selectedIndex ? styles.dotActive : {}),
            }}
            onClick={() => setSelectedIndex(index)}
          />
        ))}
      </div>

      {/* Action Buttons Row - Play, Leaderboard, Settings */}
      <div style={styles.buttonRow}>
        <button style={styles.playButton} onClick={handlePlay}>
          Play
        </button>
        <button style={styles.leaderboardButton} onClick={handleLeaderboard}>
          Leaderboard
        </button>
        <button style={styles.settingsButton} onClick={handleSettings} title="Settings">
          <GearIcon />
        </button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#FEAC64', // Mfers arcade orange
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    marginBottom: '32px',
    marginTop: '20px',
  },
  headerLogo: {
    width: '64px',
    height: '64px',
    borderRadius: '8px',
  },
  headerTitle: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#000',
    margin: 0,
  },
  carouselSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
  },
  carouselButton: {
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    border: 'none',
    borderRadius: '50%',
    width: '44px',
    height: '44px',
    cursor: 'pointer',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  carouselContainer: {
    width: '280px',
    height: '180px',
    overflow: 'hidden',
    borderRadius: '12px',
    border: '3px solid #000',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
  },
  levelImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  levelName: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#000',
    marginBottom: '16px',
    textAlign: 'center',
  },
  dotsContainer: {
    display: 'flex',
    gap: '8px',
    marginBottom: '24px',
  },
  dot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  },
  dotActive: {
    backgroundColor: '#000',
  },
  buttonRow: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
    alignItems: 'center',
  },
  playButton: {
    backgroundColor: '#16a085', // Teal green
    color: '#fff',
    border: '2px solid #000',
    borderRadius: '8px',
    padding: '12px 36px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    height: '48px',
    boxSizing: 'border-box',
  },
  leaderboardButton: {
    backgroundColor: '#e94560', // Pink/red accent like mfersarcade
    color: '#fff',
    border: '2px solid #000',
    borderRadius: '8px',
    padding: '12px 18px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    height: '48px',
    boxSizing: 'border-box',
  },
  settingsButton: {
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    border: '2px solid #000',
    borderRadius: '8px',
    width: '48px',
    height: '48px',
    cursor: 'pointer',
    color: '#000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
  },
  authSection: {
    marginBottom: '20px',
  },
  userRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    backgroundColor: 'rgba(0, 0, 0, 0.15)',
    borderRadius: '12px',
    padding: '12px 16px',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#000',
  },
  userName: {
    fontWeight: 'bold',
    fontSize: '14px',
  },
  logoutButton: {
    backgroundColor: 'transparent',
    color: '#000',
    border: '1px solid #000',
    borderRadius: '6px',
    padding: '6px 12px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  signInButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: '#000',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '300px',
    width: '90%',
    textAlign: 'center',
  },
  modalTitle: {
    margin: '0 0 12px 0',
    fontSize: '18px',
    color: '#000',
  },
  modalText: {
    margin: '0 0 20px 0',
    fontSize: '14px',
    color: '#666',
  },
  modalButtons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
  },
  modalCancelButton: {
    backgroundColor: '#eee',
    color: '#333',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    cursor: 'pointer',
  },
  modalConfirmButton: {
    backgroundColor: '#e94560',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    cursor: 'pointer',
  },
};

export default MfersBeheadedNewLanding;
